import * as React from "react";
import * as Highcharts from "highcharts";
import highchartsGantt from "highcharts/modules/gantt";
import HighchartsReact from "highcharts-react-official";
import Exporting from "highcharts/modules/exporting";
import draggable from "highcharts/modules/draggable-points";
import ResizeObserver from "resize-observer-polyfill";
import debounce from "lodash-es/debounce";
import { K2Boolean } from "../../common/communication.base";
import { ColorProps } from "../K2Gantt";

highchartsGantt(Highcharts);
Exporting(Highcharts);
draggable(Highcharts);

export interface GOptions extends Highcharts.Options {
  xAxis?: GXAxisOptions | Array<GXAxisOptions>;
}

interface GXAxisOptions extends Highcharts.XAxisOptions {
  dateTimeLabelFormats?: GAxisDateTimeLabelFormatsOptions;
}

interface GAxisDateTimeLabelFormatsOptions extends Highcharts.AxisDateTimeLabelFormatsOptions {
  day?: GAxisDateTimeLabelFormatsOptionsObject;
  week?: GAxisDateTimeLabelFormatsOptionsObject;
}

interface GAxisDateTimeLabelFormatsOptionsObject extends Highcharts.AxisDateTimeLabelFormatsOptionsObject {
  list?: string[];
}

interface GanttCommonProps {
  dataitems: Highcharts.GanttPointOptionsObject[];
  seriesdataitems: Highcharts.SeriesGanttOptions[];
  categories: string[];
  yAxisOptions: Highcharts.YAxisOptions;
  inEditMode: boolean;
  byOperations: boolean;
  byResources: boolean;
  headerAtBottom: K2Boolean;
  ownColors: boolean;
  hideNavigator: boolean;
  hideXAxis: boolean;
  showDateTime: boolean;
  colors: ColorProps;
  handleClick: (e: Highcharts.PointClickEventObject) => void;
  handleDrop: (e: Highcharts.PointDropEventObject) => void;
  getApi?: (
    ref: React.RefObject<{
      chart: Highcharts.Chart;
      container: React.RefObject<HTMLDivElement>;
    }>
  ) => void;
}

interface GanttCommonState {
  chartOptions: GOptions;
}

export class K2GanttCommon extends React.Component<GanttCommonProps, GanttCommonState> {
  private gantt = React.createRef<{ chart: Highcharts.Chart; container: React.RefObject<HTMLDivElement> }>();
  private ro: ResizeObserver;
  private div: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private ganttHeight: number;

  constructor(props: GanttCommonProps) {
    super(props);

    Highcharts.setOptions({
      lang: {
        months: ["Leden", "Únor", "Březen", "Duben", "Květěn", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"],
        shortMonths: ["Led", "Únor", "Bře", "Dub", "Kvě", "Čer", "Čec", "Srp", "Zář", "Říj", "Lis", "Pro"],
        weekdays: ["Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota", "Neděle"],
        shortWeekdays: ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"],
        rangeSelectorFrom: "Od",
        rangeSelectorTo: "Do",
        downloadCSV: "Stáhnout CSV",
        downloadJPEG: "Stáhnout JPEG",
        downloadPDF: "Stáhnout PDF",
        downloadPNG: "Stáhnout PNG",
        downloadSVG: "Stáhnout SVG",
        downloadXLS: "Stáhnout XLS",
        viewFullscreen: "Zobrazit na celou obrazovku",
        exitFullscreen: "Opustit celou obrazovku",
        printChart: "Vytisknout",
      },
    });

    const ganttOptions: GOptions = {
      chart: {
        backgroundColor: this.getBgColor(),
        spacing: [21, 16, 0, 0], // 21 = odsazeni shora, 16 = odsazeni pro scrollbar
        style: {
          fontFamily: "inherit",
        },
        animation: false,
      },
      xAxis: [
        {
          visible: !this.props.hideXAxis,
          opposite: this.props.headerAtBottom === 0 ? true : false,
          currentDateIndicator: {
            width: 2,
            dashStyle: "Solid",
            color: "red",
            label: {
              format: "%Y-%m-%d",
            },
          },
          minTickInterval: 1000 * 60 * 60 * 24,
          minorGridLineColor: this.props.colors.ContentFrame1,
          type: "datetime",
          minorTickInterval: "auto",
          dateTimeLabelFormats: {
            day: {
              list: [`${this.props.showDateTime ? "%d.%m." : ""} %H:%M`, "%a, %e. %b", "%d.%m.", "%d"],
            },
            week: {
              list: ["Týden %W"],
            },
          },
          grid: {
            cellHeight: 27,
            borderColor: this.props.colors.ContentFrame1,
          },
          labels: {
            padding: 5,
            style: {
              fontSize: "11px",
              color: this.props.colors.DataBrowseColorFrg,
            },
            align: "center",
          },
        },
        {
          visible: !this.props.hideXAxis,
          opposite: this.props.headerAtBottom === 0 ? true : false,
          type: "datetime",
          grid: {
            cellHeight: 27,
            borderColor: this.props.colors.ContentFrame1,
          },
          dateTimeLabelFormats: {
            week: {
              list: ["Týden %W"],
            },
          },
          labels: {
            style: {
              fontSize: "11px",
              color: this.props.colors.DataBrowseColorFrg,
            },
            align: "center",
          },
        },
      ],
      navigator: {
        enabled: !this.props.hideNavigator,
        series: {
          type: "gantt",
          pointPadding: 0.25,
          pointWidth: null,
        },
        yAxis: {
          reversed: true,
          categories: [],
        },
        xAxis: {
          gridLineColor: this.props.colors.ContentFrame1,
          labels: {
            style: {
              color: this.props.colors.DataBrowseColorFrg,
            },
          },
        },
        outlineColor: this.props.colors.ContentFrame1,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        gantt: {
          grouping: true,
          borderRadius: 0,
          borderWidth: 1,
          pointWidth: null,
          dataLabels: {
            format: "{point.description}",
          },
          allowPointSelect: true,
          dragDrop: {
            dragPrecisionX: 1000 * 60 * 60 * 24,
            draggableX: this.props.inEditMode,
            draggableY: this.props.inEditMode,
          },
          point: {
            events: {
              click: this.props.handleClick,
              drop: this.props.handleDrop,
            },
          },
        },
      },
      tooltip: {
        enabled: true,
        pointFormatter: function () {
          const point = this as Highcharts.GanttPointOptionsObject;
          if (point.custom.htmlTooltip) return point.custom.htmlTooltip;

          // prettier-ignore
          return `
            <b>${this.name}</b><br>Začátek: ${new Date(this.x).toLocaleString('cs-CZ', { timeZone: 'UTC' })}<br>Konec: ${new Date(this.x2).toLocaleString('cs-CZ', { timeZone: 'UTC' })}<br>${point.custom.workTime} ${point.custom.workTimeUnit}<br>${100*(point.completed as number)} %
          `;
        },
      },
      scrollbar: {
        enabled: false,
      },
      rangeSelector: {
        enabled: false,
        buttons: [
          {
            type: "month",
            count: 1,
            text: "1m",
          },
          {
            type: "month",
            count: 3,
            text: "3m",
          },
          {
            type: "month",
            count: 6,
            text: "6m",
          },
          {
            type: "ytd",
            text: "YTD",
          },
          {
            type: "year",
            count: 1,
            text: "1r",
          },
          {
            type: "all",
            text: "Max",
          },
        ],
      },
      exporting: {
        enabled: false,
      },
      series: this.props.seriesdataitems,
      yAxis: {
        ...this.props.yAxisOptions,
        gridLineColor: this.props.colors.ContentFrame1,
        grid: {
          borderColor: this.props.colors.ContentFrame1,
        },
      },
    };

    this.state = { chartOptions: ganttOptions };
  }

  getBgColor = () => {
    return !this.props.inEditMode ? this.props.colors.DataBrowseColorBck : this.props.colors.DataChangeColorBck;
  };

  componentDidUpdate(prevProps: GanttCommonProps) {
    this.gantt.current.chart.zoomOut();

    if (prevProps.seriesdataitems !== this.props.seriesdataitems || prevProps.yAxisOptions !== this.props.yAxisOptions) {
      this.setState({
        chartOptions: {
          series: this.props.seriesdataitems,
          yAxis: this.props.yAxisOptions,
        },
      });
    }

    if (this.props.inEditMode !== prevProps.inEditMode) {
      this.setState({
        chartOptions: {
          chart: {
            backgroundColor: this.getBgColor(),
          },
          plotOptions: {
            gantt: {
              dragDrop: {
                draggableX: this.props.inEditMode,
                draggableY: this.props.inEditMode,
              },
            },
          },
        },
      });
    }
  }

  debounce = debounce((ganttHeight: number, containerHeight: number) => {
    if (this.ganttHeight == null) this.ganttHeight = ganttHeight;

    this.setState(
      {
        chartOptions: {
          chart: {
            height: containerHeight < this.ganttHeight ? containerHeight : null,
            scrollablePlotArea: {
              minHeight: containerHeight < this.ganttHeight ? this.ganttHeight : 1,
            },
          },
        },
      },
      () => this.gantt.current.chart.reflow()
    );
  }, 250);

  componentDidMount() {
    if (this.props.getApi) {
      this.props.getApi(this.gantt);
    }

    this.ro = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      let ganttHeight: number = this.gantt.current.chart.chartHeight;
      let containerHeight: number = entries[0].contentRect.height;

      this.debounce(ganttHeight, containerHeight);
    });

    this.ro.observe(this.div.current);
  }

  componentWillUnmount() {
    this.ro.unobserve(this.div.current);
  }

  render() {
    return (
      <div
        className="gantt-wrapper"
        ref={this.div}
        style={{
          height: "100%",
          width: "100%",
          display: "block",
          backgroundColor: this.getBgColor(),
        }}
      >
        <HighchartsReact ref={this.gantt} highcharts={Highcharts} options={this.state.chartOptions} constructorType={"ganttChart"} />
      </div>
    );
  }
}
